import { useQuery } from '@apollo/client';

import { MyTenantsDocument, type MemberRole } from '@/__generated__/graphql';
import { useWorkspace } from '@/core/providers/WorkspaceProvider';

/**
 * The signed-in user's role in the CURRENTLY-bound workspace, or `null` until it
 * is known.
 *
 * Reads the always-authorized `MyTenants` directory — which carries the caller's
 * role per workspace (the same seam `WorkspaceSettingsPage` and `ChannelBCard`
 * use) — and matches it to the active workspace. Returns `null` while the
 * directory loads or when the active workspace isn't in it yet.
 *
 * This is a CLIENT gate only — it exists to avoid dead-end controls (showing a
 * button that the server would reject). The server is the real authority: the
 * caller decides what a `null` (unknown) role means. For an owner-only affordance,
 * treat `null` as "allow" so an owner never sees a flash of the hidden state
 * (the ChannelBCard "only once the tier has RESOLVED" pattern), and hide only
 * once the role is KNOWN to be below the bar.
 */
export function useCurrentTenantRole(): MemberRole | null {
  const { currentWorkspace } = useWorkspace();
  const { data } = useQuery(MyTenantsDocument, { fetchPolicy: 'cache-first' });
  // Null-safe on `myTenants`: a partial / error-policy response can resolve
  // `data` without the field, and this hook must degrade to "role unknown"
  // (null → fail-safe) rather than throw during render. `?? []` covers both the
  // undefined-`data` case (typed) and a `data`-without-`myTenants` runtime shape.
  const mine = (data?.myTenants ?? []).find((tenant) => tenant.id === currentWorkspace?.id);
  return mine?.role ?? null;
}
